import Toaster from "@/components/toaster";
import { accountHref } from "@/utils/apprenticeship-links";
import { EmptyArea } from "@/components/ui/empty-area";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useUser } from "@/providers/user";
import { DataService } from "@/services/data";
import { ApprenticeshipRegistrationMapType } from "@/types/apprenticeship/apprentice";
import { ApprenticeshipEnterpriseType, ApprenticeshipInterviewType } from "@/types/apprenticeship/enterprise";
import { PaginatedResponse } from "@/types/common";
import { commute_transports } from "@/utils/constants";
import { isFeatureEnabled } from "@/utils/feature-flag";
import { ExternalLink, ExternalLinkIcon } from "lucide-react";
import { DateTime } from "luxon";
import "mapbox-gl/dist/mapbox-gl.css";
import { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";

export interface RegistrationsMapSidebarProps extends React.HTMLAttributes<HTMLDivElement> {
    registration?: ApprenticeshipRegistrationMapType;
    company?: ApprenticeshipEnterpriseType;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    defaultOpen?: boolean;
    isAdmin?: boolean;
}

export function RegistrationsMapSidebar({ registration, company, open, onOpenChange, defaultOpen, isAdmin = false, ...props }: RegistrationsMapSidebarProps) {
    const { user } = useUser();
    const [innerOpen, setInnerOpen] = useState(defaultOpen ?? false);
    const [interviews, setInterviews] = useState<PaginatedResponse<ApprenticeshipInterviewType>>();

    const fetchInterviews = async (reset: boolean = true) => {
        if (registration && registration.applications_count > 0) {
            if (reset) {
                setInterviews(undefined);
            }
            try {
                const _interviews = (await DataService.client
                    .url(`/apprenticeship/interviews/?student_id=${registration.id}&page_size=1000`)
                    .get()
                    .json()) as PaginatedResponse<ApprenticeshipInterviewType>;
                setInterviews(_interviews);
            } catch (e) {
                console.error(e);
                Toaster.error("Une erreur s'est produite lors de la récupération des candidatures");
            }
        }
    };

    useEffect(() => {
        if (registration) {
            setInnerOpen(true);
            fetchInterviews(true);
        }
    }, [registration]);

    const _onOpenChange = (open: boolean) => {
        if (onOpenChange) {
            onOpenChange(open);
        }
        setInnerOpen(open);
    };

    const distance_in_kms = (registration: ApprenticeshipRegistrationMapType) => {
        if (company && company.latitude && company.longitude && registration.latitude && registration.longitude) {
            function toRadians(degrees: number): number {
                return (degrees * Math.PI) / 180;
            }
            const earthRadiusKm = 6371; // Earth's radius in kilometers
            const dLat = toRadians(company.latitude - registration.latitude);
            const dLon = toRadians(company.longitude - registration.longitude);

            const lat1 = toRadians(registration.latitude);
            const lat2 = toRadians(company.latitude);

            const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.sin(dLon / 2) * Math.sin(dLon / 2) * Math.cos(lat1) * Math.cos(lat2);
            const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

            return earthRadiusKm * c; // Distance in kilometers
        }
        return NaN;
    };

    const google_map_itinerary_url = (registration: ApprenticeshipRegistrationMapType) => {
        if (company) {
            let travelmode = "walking";
            if (registration.commute_transportation) {
                if (registration.commute_transportation.includes("voiture")) {
                    travelmode = "driving";
                } else if (registration.commute_transportation.includes("velo")) {
                    travelmode = "bicycling";
                } else if (registration.commute_transportation.includes("transport_commun")) {
                    travelmode = "transit";
                }
            }
            return `https://www.google.com/maps/dir/?api=1&origin=${encodeURIComponent(`${registration.latitude},${registration.longitude}`)}&destination=${encodeURIComponent(`${company.latitude},${company.longitude}`)}&travelmode=${travelmode}`;
        }
        return "";
    };

    const useSfOpportunity = isFeatureEnabled("apprenticeship-standard-offers");
    const registrationDetailHref =
        useSfOpportunity && registration?.sf_opportunity_id
            ? `/opportunities/${registration.sf_opportunity_id}`
            : `/alternance/candidats/${registration?.id}`;

    return (
        <Sheet open={open !== undefined ? open : innerOpen} onOpenChange={_onOpenChange} modal={false} {...props}>
            <SheetContent onInteractOutside={e => e.preventDefault()} className="w-[450px]">
                {registration ? (
                    <SheetHeader>
                        <SheetTitle>
                            {registration.first_name} {registration.last_name}{" "}
                            <NavLink to={registrationDetailHref}>
                                <ExternalLink className="ml-0.5 inline-block h-4 w-4" />
                            </NavLink>
                        </SheetTitle>
                        <div className="space-y-5">
                            <div className="space-y-3">
                                {registration.want_to_move_out && (
                                    <div>
                                        <p className="text-xs text-gray-600">Souhaite déménager à</p>
                                        <p>
                                            {registration.move_out_city} {registration.move_out_postal_code}
                                        </p>
                                    </div>
                                )}
                                <div>
                                    <p className="text-xs text-gray-600">Adresse{registration.want_to_move_out && " actuelle"}</p>
                                    <p>
                                        {registration.street}
                                        <br />
                                        {registration.city} {registration.postal_code}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-600">Date de naissance</p>
                                    <p>
                                        {registration.birth_date ? (
                                            `${DateTime.fromISO(registration.birth_date).toLocaleString(DateTime.DATE_MED)} (${Math.floor(Math.abs(DateTime.fromISO(registration.birth_date).diffNow("years").years))} ans)`
                                        ) : (
                                            <span className="italic text-gray-700">Non spécifié</span>
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-600">Mode de transport</p>
                                    <p>
                                        {registration.commute_transportation ? (
                                            registration.commute_transportation
                                                ?.split(",")
                                                .map(t => commute_transports.filter(_t => _t.id == t)[0]?.label)
                                                .filter(_ => !!_)
                                                .join(", ")
                                        ) : (
                                            <span className="italic text-gray-700">Non spécifié</span>
                                        )}
                                    </p>
                                </div>
                                <div>
                                    <p className="text-xs text-gray-600">Temps de trajet maximum</p>
                                    <p>{registration.commute_max_duration ? `${registration.commute_max_duration} minutes` : <span className="italic text-gray-700">Non spécifié</span>}</p>
                                </div>

                                {company && !registration.want_to_move_out && (
                                    <div>
                                        <p className="text-xs text-gray-600">Distance</p>
                                        <p>
                                            <NavLink to={google_map_itinerary_url(registration)} target="_blank">
                                                <span className="flex items-center">
                                                    {Math.round(distance_in_kms(registration) * 10) / 10} km <ExternalLink className="ml-1 inline-block h-3 w-3" />
                                                </span>
                                            </NavLink>
                                        </p>
                                    </div>
                                )}

                                <div>
                                    <p className="text-xs text-gray-600">CAP</p>
                                    <p>
                                        {registration.user_cap ? `${registration.user_cap.first_name} ${registration.user_cap.last_name}` : <span className="italic text-gray-700">Non spécifié</span>}
                                    </p>
                                </div>

                                {registration.status === "Submitted" && (
                                    <div>
                                        <p className="text-xs text-gray-600">Jury d'admission</p>
                                        <p>
                                            {registration.admission_appointment_at ? (
                                                DateTime.fromISO(registration.admission_appointment_at).toLocaleString(DateTime.DATETIME_MED)
                                            ) : (
                                                <span className="italic text-gray-700">Non spécifié</span>
                                            )}
                                        </p>
                                    </div>
                                )}

                                {isAdmin ||
                                    (registration.user_ce?.crm_user_id != user?.id && (
                                        <div>
                                            <p className="text-xs text-gray-600">Responsable entreprise</p>
                                            <p>
                                                {registration.user_ce ? (
                                                    `${registration.user_ce.first_name} ${registration.user_ce.last_name}`
                                                ) : (
                                                    <span className="italic text-gray-700">Aucun responsable</span>
                                                )}
                                            </p>
                                        </div>
                                    ))}
                            </div>

                            {registration.applications_count > 0 ? (
                                <div>
                                    <div>
                                        <h3 className="mb-2 font-semibold">
                                            {registration.applications_count} candidature{registration.applications_count > 1 ? "s" : ""}
                                        </h3>
                                        <div className="space-y-2">
                                            {interviews !== undefined &&
                                                interviews.results.map(interview => (
                                                    <div key={`offer_${interview.id}`} className="rounded-sm border p-4">
                                                        <h4>
                                                            <NavLink to={accountHref(interview.offer.company.sf_account)}>
                                                                <span className="flex items-center">
                                                                    {interview.offer.company.name} <ExternalLinkIcon className="ml-1 h-3 w-3" />
                                                                </span>
                                                            </NavLink>
                                                        </h4>
                                                        <p>{interview.interview_datetime ? DateTime.fromISO(interview.interview_datetime).toLocaleString() : "CV déposé"}</p>
                                                        <p className="text-sm text-gray-500">
                                                            {interview.offer.company.city} - {interview.offer.company.postal_code}
                                                        </p>
                                                    </div>
                                                ))}
                                        </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </SheetHeader>
                ) : (
                    <EmptyArea>Sélectionner un candidat</EmptyArea>
                )}

                <div className="mt-5"></div>
            </SheetContent>
        </Sheet>
    );
}
